import React from "react";
import { useSnackbar } from "notistack";

// Mui core
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormHelperText from "@mui/material/FormHelperText";

// Hooks
import { useFetchUser } from "../../../../hooks/useFetchUser";

// Settings
import settings from "../../../../settings.json";

// Validation
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// Utils
import { emailRegex } from "../../../../utils/regex";

// Firebase
import { httpsCallable } from "firebase/functions";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth, functions, firestore } from "../../../../firebase/firebase";
import { collection, addDoc, updateDoc, doc } from "firebase/firestore";

// Interfaces
import { AdminUser } from "../../../../interfaces/AdminUser";

// yup validation
const requiredMessage = "Required field";

const userSchema = yup.object({
    firstName: yup.string().required(requiredMessage),
    lastName: yup.string().required(requiredMessage),
    email: yup.string().matches(emailRegex, "Invalid email").required(requiredMessage),
    roles: yup.array(yup.string().oneOf(settings.app.dashboardRoles)).min(1, "At least one role required").required(requiredMessage),
});

interface Props {
    open: boolean;
    setOpen: any;
    userId: string;
    onSuccess?: () => void;
}

const UserDialog: React.FC<Props> = ({ open, setOpen, onSuccess }) => {
    const { currentAdmin, loading, setLoading } = useFetchUser();
    const { enqueueSnackbar } = useSnackbar();

    const defaultValues = {
        firstName: "",
        lastName: "",
        email: "",
        roles: [],
    };

    // Forms
    const {
        handleSubmit,
        formState: { errors },
        control,
    } = useForm({ resolver: yupResolver(userSchema), defaultValues });

    const onSubmit = async (data: AdminUser) => {
        try {
            const user: AdminUser = { ...data };
            setLoading(true);
            const createAdminUser = httpsCallable(functions, "createAdminUser");
            const result = await createAdminUser(user);

            if (result.data) {
                // Send password setup email via Firebase built-in (no SendGrid dependency)
                try {
                    await sendPasswordResetEmail(auth, data.email);
                } catch (emailErr) {
                    console.error("Password reset email failed:", emailErr);
                }

                const logPayload = await addDoc(collection(firestore, "Logs"), {
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    isDeleted: false,
                    adminId: currentAdmin && currentAdmin.id ? currentAdmin.id : "",
                    reason: `Created admin ${data && (data as any).email ? (data as any).email : (data as any).id}`,
                });

                await updateDoc(doc(firestore, "Logs", logPayload.id), { id: logPayload.id });

                enqueueSnackbar("Created admin — a password setup email has been sent", { variant: "success" });
                setOpen(false);
                if (onSuccess) onSuccess();
            }
        } catch (e: any) {
            console.error(e);
            enqueueSnackbar(e.message || "An error occurred while creating the admin", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => !loading && setOpen(false)}
            aria-labelledby="scroll-dialog-title"
            aria-describedby="scroll-dialog-description"
        >
            <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle id="scroll-dialog-title">Admin creation</DialogTitle>
                <DialogContent>
                    <Controller
                        render={({ field }) => (
                            <TextField
                                variant="standard"
                                helperText={errors.firstName?.message}
                                error={!!errors.firstName?.message}
                                label="Firstname"
                                fullWidth
                                required
                                {...field}
                            />
                        )}
                        name="firstName"
                        control={control}
                    />
                    <Controller
                        render={({ field }) => (
                            <TextField
                                variant="standard"
                                helperText={errors.lastName?.message}
                                error={!!errors.lastName?.message}
                                label="Lastname"
                                fullWidth
                                required
                                {...field}
                            />
                        )}
                        name="lastName"
                        control={control}
                    />
                    <Controller
                        render={({ field }) => (
                            <TextField
                                variant="standard"
                                helperText={errors.email?.message}
                                error={!!errors.email?.message}
                                label="Email"
                                fullWidth
                                required
                                {...field}
                            />
                        )}
                        name="email"
                        control={control}
                    />
                    <Controller
                        name="roles"
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth variant="standard" error={!!errors.roles?.message}>
                                <InputLabel id="roles-id">Roles *</InputLabel>
                                <Select
                                    labelId="roles-id"
                                    multiple
                                    {...field}
                                    inputProps={{ readOnly: currentAdmin && currentAdmin.roles.includes(settings.app.highestRole) ? false : true }}
                                >
                                    {settings.app.dashboardRoles.map(r => (
                                        <MenuItem key={r} value={r}>
                                            {r}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.roles?.message && <FormHelperText>{errors.roles.message}</FormHelperText>}
                            </FormControl>
                        )}
                    />
                    <DialogActions>
                        <Button variant="contained" onClick={() => setOpen(false)} color="primary">
                            Cancel
                        </Button>
                        <Button variant="contained" type="submit" color="primary">
                            Add
                        </Button>
                    </DialogActions>
                </DialogContent>
            </form>
        </Dialog>
    );
};

export default UserDialog;
